<template>
  <app-content>
    <app-content-body>

      <div class="inner">
        <p class="buttons">
          <a
              class="btn btn_action btn_add"
              title="Přidat stanici"
              @click.prevent="createItem"
          >Přidat stanici</a>
        </p>
        <h1 v-translate>TV stanice</h1>
      </div>

      <app-table
          clickable
          :data="items"
          highlight-rows-on-hover
          :order="params.order"
          :page-size="params.limit"
          @sort="sortItems"
          @rowClick="goToTvStationDetail"
          @deleteRow="deleteItem"
          :loading="listLoading || stationGroupsPending"
      >
        <template slot="header">
          <app-table-header-item class="field_left">
            <app-input
                :label="translations.search"
                type="search"
                v-model="params.search"
                @input="fetchItems(true)"
                full-width
            />
          </app-table-header-item>
          <app-table-header-item class="field_right">
            <app-table-header-select :label="translations.group">
              <app-select
                  :options="groupSelect"
                  v-model.trim="params.groups"
                  @change="fetchItems(true)"
              />
            </app-table-header-select>
          </app-table-header-item>
          <app-table-header-item class="field_left">
            <app-table-header-select :label="translations.type">
              <app-select
                  :options="typeSelect"
                  v-model.trim="params.type"
                  @change="fetchItems(true)"
              />
            </app-table-header-select>
          </app-table-header-item>
          <app-table-header-item class="field_right">
              <app-table-header-select :label="translations.maturityRating">
                  <app-select
                      :options="maturityRatingSelect"
                      v-model.trim="params.maturityRatingId"
                      @change="fetchItems(true)"
                  />
              </app-table-header-select>
          </app-table-header-item>
        </template>

        <app-table-column first sortable attribute="name" :label="translations.name">
          <template slot-scope="row">
            <strong>
              {{ row.name }}
            </strong>
          </template>
        </app-table-column>
        <app-table-column sortable attribute="_id" label="ID" />
        <app-table-column sortable attribute="type" :label="translations.type">
          <template slot-scope="row">
            {{ enumsMap.get(row.type) }}
          </template>
        </app-table-column>
        <app-table-column sortable sort-name="numberOfChannels" :label="translations.numberOfTvChannels">
          <template slot-scope="row">
            {{ row.numberOfChannels }}
          </template>
        </app-table-column>
        <app-table-column sortable attribute="languageName" :label="translations.language">
          <template slot-scope="row">
            {{ row.languageName }}
          </template>
        </app-table-column>
        <app-table-column sortable attribute="importedLanguage" :label="translations.importLanguage">
          <template slot-scope="row">
            {{ row.importLanguage }}
          </template>
        </app-table-column>
        <app-table-column sortable sort-name="lastImport" :label="translations.lastImport">
          <template slot-scope="row">
              <div v-html="formatDate(row._lastImport)" />
          </template>
        </app-table-column>
        <app-table-column sortable sort-name="updated" :label="translations.updated">
          <template slot-scope="row">
            <div v-html="formatDate(row._updated)" />
          </template>
        </app-table-column>

        <template slot="footer" v-if="itemsMetadata.totalCount > params.limit">
          <app-table-pagination
            :page="listPage"
            :itemsPerPage="params.limit"
            :totalCount="itemsMetadata.totalCount"
            @pageChanged="tablePageChanged"
          />
        </template>
      </app-table>
    </app-content-body>
  </app-content>
</template>

<script>
import { prop, length, compose } from 'ramda'
import { mapActions, mapState } from 'vuex'
import appTitleMixin from 'mixins/appTitleMixin'
import getItemsListMixin from 'mixins/getItemsListMixin'
import formatDateMixin from 'mixins/formatDateMixin'

const itemsListMixin = getItemsListMixin({
  apiMethodsSuffix: 'TvStations',
  enumsMap: true
})

export default {
  name: 'tv-stations',
  mixins: [formatDateMixin, itemsListMixin, appTitleMixin()],
  created() {
    this.getTvStationGroups({
      params: {
        order: 'name'
      }
    })

    this.getMaturityRatings({params: { order: 'name' }});

    this.params.idLanguage = this.currentLanguageId
  },
  computed: {
    ...mapState({
      stationGroups: state => state['tvStationGroups']['tvStationGroups'],
      stationGroupsPending: state => state['tvStationGroups'].pending['tvStationGroups'],
      currentLanguageId: state => state.uiLanguages.currentLanguageId,
      maturityRatings: state => state.maturityRatings.maturityRatings,
    }),
    translations() {
      return {
        search: this.$gettext('Vyhledávání'),
        group: this.$gettext('Skupina'),
        name: this.$gettext('Název'),
        language: this.$gettext('Jazyk'),
        importLanguage: this.$gettext('Importovaný jazyk'),
        type: this.$gettext('Typ'),
        maturityRating: this.$gettext('Věková hranice'),
        numberOfTvChannels: this.$gettext('Počet TV kanálů'),
        lastImport: this.$gettext('Poslední import'),
        updated: this.$gettext('Upraveno')
      }
    },
    groupSelect() {
      return [
        { name: this.$gettext('Všechny skupiny'), value: '' },
        ...this.stationGroups.map(v => ({ name: v.name, value: v._id }))
      ]
    },
    maturityRatingSelect() {
        return [
            { name: this.$gettext('Všechny věkové hranice'), value: null },
            { name: this.$gettext('Bez vybrané věkové hranice'), value: -1 },
            { name: this.$gettext('S vybranou věkovou hranicí'), value: -2 },
            ...this.maturityRatings.map(v => ({ name: v.name, value: v._id }))
        ]
    },
    typeSelect() {
      return [
        { name: this.$gettext('Všechny typy'), value: '' },
        { name: this.$gettext('EPG PLUS'), value: 'TV_STATION_EPG_PLUS' },
        { name: this.$gettext('EPG REST'), value: 'TV_STATION_EPG_REST' }
      ]
    },
    currentTitle() {
      return [
        this.$gettext('TV stanice')
      ]
    }
  },
  methods: {
    ...mapActions(['getTvStationGroups', 'getMaturityRatings']),
    getChannelsCount: compose(length, prop('tvChannelIds')),
    getDeleteMessage(item) {
      return this.$gettext('Počet navázaných kanálů') + ': ' + this.getChannelsCount(item)
    },
    goToTvStationDetail(tvStation) {
      this.$router.push({
        name: 'tv-station',
        params: {
          id: tvStation._id
        }
      })
    }
  }
}
</script>
